import { useLazyQuery, useMutation } from '@apollo/client';
import CustomizeLayoutQuiz from 'component/layout_quiz/layout_quiz';
import { useAuth } from 'contexts/AuthContext';
import { useEffect, useState, useCallback } from 'react';
import _valuesIn from 'lodash/valuesIn';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch } from 'react-redux';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { CREATE_ANSWER } from '../../../graph_ql/getQuiz/mutation';
import { GET_QUESTION_DATA, GET_QUIZ_RESPONSE } from '../../../graph_ql/getQuiz/queries';
import { PATH } from '../../../routes/constant';
import { useStyles } from '../fashion_restriction/styles';
import { useGetGender } from '../../../hooks/useGetGender';
import { EGENDER, EQuizId, ESubjectQuestion, quizContextType } from 'types/quiz';
import { setShowOffDefault } from 'redux/authentication/user_detail';
import { getAnsweredQuestions } from 'utils/convertListString';
import MultipleSelectOptions from 'component/MultiSelectOptions';
import useValidateAnswers from '../hooks/useValidateAnswers';

interface IQuestionData {
    value?: string;
    id?: string;
    title?: string;
    isSelect?: boolean;
}

interface Ianswer {
    title?: string;
    value?: string;
    id?: String;
    answerId?: string;
    userProperty?: string;
    answerUpdated?: string;
    action?: string;
    isSkipped?: string;
    required?: boolean;
    options?: IQuestionData[];
    questionDataSubjectTags?: string[];
    questionDataSubject?: string;
}

interface Imutation {
    questionId: String;
    questionSubject: String;
    contextType: String;
    contextId: String;
    value: String;
    valueType: String;
    id?: String;
    questionDataIds: any[];
}

interface IProps {
    handleNext?: (value: Boolean, order?: number, gender?: EGENDER) => void;
}

const ShowOff = (props: IProps) => {
    const { handleNext } = props;
    const { userName } = useParams<{ userName: string }>();
    const { currentUser } = useAuth();
    const gender = useGetGender(Boolean(userName));
    const [showOffQuizList, setShowOffQuizList] = useState<Ianswer[]>([]);
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();
    const classes = useStyles();
    const [questionsLoading, setQuestionsLoading] = useState(false);
    const [answerLoading, setAnswerLoading] = useState(false);
    const [customValue, setCustomvalue] = useState('');
    const [customValueError, setCustomValueError] = useState(false);
    const [quizData, setQuizData] = useState<any>(null);
    const isUpdate = location.pathname.includes(PATH.MY_STYLE_PREFERENCES);
    const [answerAlreadyUpdated, setAnswerUpdated] = useState(false);
    const isValidAnswer = useValidateAnswers(
        isUpdate,
        showOffQuizList?.filter((item: any) => item?.options?.filter((item) => item.isSelect).length > 0).length > 0,
        answerAlreadyUpdated,
    );

    const [getQuestionData, { loading: loadingQuestionData }] = useLazyQuery(GET_QUESTION_DATA, {
        onCompleted(data) {
            const list = showOffQuizList.map((item) => {
                return {
                    ...item,
                    options:
                        data?.getQuestionData?.data?.map((questionData) => {
                            if (item?.value) {
                                let parsedValue = JSON.parse(item?.value || '');
                                const answeredShowOff = parsedValue?.find((i) => questionData.id === i.id);
                                if (answeredShowOff && answeredShowOff?.id === questionData?.id) {
                                    if (answeredShowOff.customValue) {
                                        setCustomvalue(answeredShowOff.customValue);
                                    }
                                    return { ...questionData, isSelect: true };
                                } else {
                                    return questionData;
                                }
                            } else {
                                return questionData;
                            }
                        }) || [],
                };
            });
            setShowOffQuizList(list);
        },
    });

    useEffect(() => {
        if (showOffQuizList.length) {
            let firstShowOffQuiz = showOffQuizList[0];
            if (!firstShowOffQuiz.options?.length) {
                getQuestionData({
                    variables: {
                        input: {
                            subject: ESubjectQuestion.SHOW_OFF,
                            paging: { page: 0, size: 0 },
                            enabled: true,
                        },
                    },
                });
            }
        }
    }, [showOffQuizList, getQuestionData, gender]);

    const [getQuizResponse] = useLazyQuery(GET_QUIZ_RESPONSE, {
        onCompleted(data) {
            const listAnswers = _valuesIn(data.getQuizResponse?.answers);
            const listQuestions = _valuesIn(data.getQuizResponse?.questions);
            setQuizData(data.getQuizResponse);
            dispatch(
                setShowOffDefault(getAnsweredQuestions(data.getQuizResponse?.answers, data.getQuizResponse?.questions)),
            );
            const list = listQuestions.map((item) => {
                const answeredShowOff = listAnswers.find((i) => i.questionId === item.id);
                let options: IQuestionData[] = [];
                if (showOffQuizList.length) {
                    let existingShowOff = showOffQuizList.find((i) => i.id === item.id);
                    if (!_isEmpty(existingShowOff) && existingShowOff?.options) {
                        options = existingShowOff?.options;
                    }
                }
                if (answeredShowOff) {
                    return {
                        ...item,
                        value: answeredShowOff.value,
                        options: options,
                        answerId: answeredShowOff.id,
                    };
                }
                return { ...item, options: options };
            });
            setShowOffQuizList(list);
            setQuestionsLoading(false);
        },
    });

    const handleRedirection = useCallback(() => {
        if (location.pathname === PATH.QUIZ_MANAGER) {
            history.push(`${PATH.QUIZ_MANAGER}/?signup=${Date.now()}`);
        }
    }, [location, history]);

    const [answerMutation] = useMutation(CREATE_ANSWER, {
        async onCompleted(response) {
            await getQuizResponse({
                variables: {
                    input: {
                        quizId: EQuizId.SHOW_OFF,
                        contextType: quizContextType,
                        contextId: EQuizId.SHOW_OFF,
                        userId: currentUser.uid,
                    },
                },
            });
            setAnswerLoading(false);
            handleRedirection();
        },
        onError(error) {
            console.log('Create Failed: ', error);
        },
    });

    const createAnswer = (input: Imutation) => {
        const variables = {
            input,
        };
        answerMutation({ variables });
    };

    useEffect(() => {
        if (currentUser && Boolean(!userName) && gender) {
            setQuestionsLoading(true);
            getQuizResponse({
                variables: {
                    input: {
                        quizId: EQuizId.SHOW_OFF,
                        contextType: quizContextType,
                        contextId: EQuizId.SHOW_OFF,
                        userId: currentUser.uid,
                    },
                },
            });
        }
    }, [currentUser, userName, gender, getQuizResponse, handleNext]);

    const renderShowOffQuiz = (slide, index) => {
        return (
            <MultipleSelectOptions
                key={index}
                slide={slide}
                loadingQuestionData={loadingQuestionData}
                handleMultiSelect={handleMultiSelect}
                customValue={customValue}
                handleTextFieldChange={handleTextFieldChange}
                customValueError={customValueError}
            />
        );
    };
    const handleMultiSelect = (option) => {
        setAnswerUpdated(true);
        let updateShowOffQuizList = showOffQuizList.map((item) => {
            return {
                ...item,
                options: item?.options?.map((d) => {
                    if (option.value === 'Nothing Specific') {
                        if (option.id === d.id) {
                            return { ...d, isSelect: !option.isSelect };
                        } else {
                            return { ...d, isSelect: false };
                        }
                    } else if (option.id === d.id) {
                        return { ...d, isSelect: !option.isSelect };
                    } else if (d.value === 'Nothing Specific') {
                        return { ...d, isSelect: false };
                    }
                    return d;
                }),
            };
        });
        setShowOffQuizList(updateShowOffQuizList);
    };

    const handleTextFieldChange = (e) => {
        setCustomvalue(e.target.value);
        if (customValueError) {
            setCustomValueError(false);
        }
    };

    const handleSaveAnswer = () => {
        if (showOffQuizList.length) {
            let quiz = showOffQuizList[0];
            let selectedOptionList: Ianswer[] = [];
            let isCustomValueMissing = false;
            quiz?.options?.forEach((item) => {
                if (item?.isSelect && item?.value?.toLowerCase() === 'other') {
                    if (customValue) {
                        selectedOptionList = [...selectedOptionList, item];
                    } else {
                        isCustomValueMissing = true;
                        setCustomValueError(true);
                    }
                } else if (item?.isSelect && item?.value?.toLowerCase() !== 'other') {
                    selectedOptionList = [...selectedOptionList, item];
                }
            });
            if (!isCustomValueMissing) {
                const selectedOptionValue = selectedOptionList?.map((item) => {
                    let selectedItem = {
                        id: item.id,
                        title: item.title,
                        value: item.value,
                    };
                    if (item.value === 'Other') {
                        return { ...selectedItem, type: 'custom', customValue };
                    } else {
                        return { ...selectedItem, type: 'default', customValue: '' };
                    }
                });
                const selectedOptionId = selectedOptionList?.map((item) => {
                    return item.id;
                });
                setAnswerLoading(true);
                createAnswer({
                    contextId: EQuizId.SHOW_OFF,
                    contextType: quizContextType,
                    questionId: quiz?.id || '',
                    questionSubject: quiz.questionDataSubject || '',
                    value: JSON.stringify(selectedOptionValue),
                    valueType: 'OBJECT_LIST',
                    id: quiz?.answerId,
                    questionDataIds: selectedOptionId || [],
                });
            }
        }
    };

    return (
        <CustomizeLayoutQuiz
            quizId={EQuizId.SHOW_OFF}
            title={Boolean(!userName) ? quizData?.subTitle : ''}
            titlePage={quizData?.title}
            handleAnswer={() => {
                handleSaveAnswer();
            }}
            isUpdate={location.pathname !== PATH.QUIZ_MANAGER}
            isDisplayContent={Boolean(!userName)}
            hideBottomNav={location.pathname === PATH.QUIZ_MANAGER}
            requiredQuestion={true}
            loading={answerLoading}
            containerLoading={questionsLoading}
            nextBtnDisabled={!isValidAnswer}
        >
            <div className={classes.container}>
                {showOffQuizList.map((slide, index) => {
                    return renderShowOffQuiz(slide, index);
                })}
            </div>
        </CustomizeLayoutQuiz>
    );
};
export default ShowOff;
